import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../common/page_component/index.js';
import Loader from '../common/Loader.js';
import DateFilterDropdown from './DateFilterDropdown.js';
import analyticsService from '../../services/AnalyticsService';
import {
  FileText,
  TrendingUp,
  Clock,
  CheckCircle,
  AlertCircle,
  ArrowLeft,
  IndianRupee,
} from 'lucide-react';
import Button from '../common/Button.js';
import { useTranslation } from 'react-i18next';
import {
  AnalyticsStatCard,
  AnalyticsTable,
  analyticsStatsGrid6Class,
  useAnalyticsCompanyScope,
  buildAnalyticsParams,
} from './common/index.js';
import { formatCurrencyWhole as formatCurrency } from '../../utils/formatCurrency.js';

const OnContractAnalytics = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [vehicleBreakdown, setVehicleBreakdown] = useState([]);
  const [totals, setTotals] = useState({
    totalContracts: 0,
    totalValue: 0,
    totalPaid: 0,
    totalPending: 0,
    fullyPaid: 0,
    unpaid: 0,
  });
  const [filter, setFilter] = useState('all');
  const [customDates, setCustomDates] = useState({ from_date: '', to_date: '' });
  const {
    currentUser,
    companies,
    selectedCompany,
    handleCompanyChange,
    ready,
  } = useAnalyticsCompanyScope({ filter, customDates });

  useEffect(() => {
    if (ready) fetchData();
  }, [filter, selectedCompany, ready]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const params = buildAnalyticsParams({
        filter,
        customDates,
        selectedCompany,
        currentUser,
      });
      const response = await analyticsService.getOnContractAnalytics(params);
      const summary = response.summary || {};
      const paymentStatus = response.payment_status || {};
      setVehicleBreakdown(response.by_vehicle || []);
      setTotals({
        totalContracts: summary.total_contracts || 0,
        totalValue: summary.total_contract_value || 0,
        totalPaid: summary.total_paid || 0,
        totalPending: summary.total_balance || 0,
        fullyPaid: paymentStatus.fully_paid || 0,
        unpaid: paymentStatus.unpaid || 0,
      });
    } catch (error) {
      console.error('Error fetching on-contract analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout fullWidth>
        <Loader fullScreen={false} size="default" message="Loading On Contract Analytics..." />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout fullWidth>
      <div className="w-full">
        {/* Header — consistent with other analytics pages */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 md:mb-6 gap-3">
          <div className="min-w-0">
            <h1 className="text-xl sm:text-2xl font-semibold text-secondary">On Contract Analytics</h1>
            <p className="text-xs sm:text-sm text-gray-600 mt-0.5">
              Monitor ON contract performance and financial metrics
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2 sm:gap-3">
            <DateFilterDropdown
              filter={filter}
              setFilter={setFilter}
              customDates={customDates}
              setCustomDates={setCustomDates}
              onApply={fetchData}
              currentUser={currentUser}
              companies={companies}
              selectedCompany={selectedCompany}
              onCompanyChange={handleCompanyChange}
              showCompanyFilter
            />
            <Button variant="primary" icon={ArrowLeft} onClick={() => navigate('/analytics')}>
              Back
            </Button>
          </div>
        </div>

        {/* Summary cards — 2 cols mobile, 3 iPad, 6 desktop; soft pastel icons + ₹ */}
        <div className={`${analyticsStatsGrid6Class} mb-4 md:mb-6`}>
          <AnalyticsStatCard
            title="Total Contracts"
            value={totals.totalContracts}
            subtitle="All contracts"
            icon={FileText}
            color="bg-blue-100 text-blue-600"
          />
          <AnalyticsStatCard
            title="Total Value"
            value={formatCurrency(totals.totalValue)}
            subtitle="Contract value"
            icon={IndianRupee}
            color="bg-emerald-100 text-emerald-600"
          />
          <AnalyticsStatCard
            title="Total Paid"
            value={formatCurrency(totals.totalPaid)}
            subtitle="Amount collected"
            icon={CheckCircle}
            color="bg-teal-100 text-teal-600"
          />
          <AnalyticsStatCard
            title="Pending"
            value={formatCurrency(totals.totalPending)}
            subtitle="Balance due"
            icon={Clock}
            color="bg-orange-100 text-orange-600"
          />
          <AnalyticsStatCard
            title="Fully Paid"
            value={totals.fullyPaid}
            subtitle="Settled contracts"
            icon={TrendingUp}
            color="bg-indigo-100 text-indigo-600"
          />
          <AnalyticsStatCard
            title="Unpaid"
            value={totals.unpaid}
            subtitle="Awaiting payment"
            icon={AlertCircle}
            color="bg-rose-100 text-rose-600"
          />
        </div>

        <AnalyticsTable
          title="Vehicle Breakdown"
          columns={[
            { header: 'Vehicle', align: 'left' },
            { header: 'Vehicle No.', align: 'left' },
            { header: 'Contracts', align: 'right' },
            { header: t('pages.shipment.title'), align: 'right' },
            { header: 'Revenue', align: 'right' },
            { header: 'Contract Value', align: 'right' },
            { header: 'Paid', align: 'right', cellClassName: 'text-emerald-600' },
            { header: 'Balance', align: 'right', cellClassName: 'text-orange-600' },
            { header: 'Trip P&L', align: 'right' },
          ]}
          rows={vehicleBreakdown.map((v, idx) => ({
            key: idx,
            cells: [
              v.vehicle_name || '-',
              v.vehicle_number || '-',
              v.contracts,
              v.shipments,
              formatCurrency(v.revenue),
              formatCurrency(v.contract_value),
              formatCurrency(v.paid),
              formatCurrency(v.balance),
              <span
                key="pl"
                className={`font-semibold ${(v.trip_pl || 0) >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}
              >
                {formatCurrency(v.trip_pl)}
              </span>,
            ],
          }))}
          emptyMessage="No data found for the selected filters"
          tableMinWidth="900px"
        />
      </div>
    </DashboardLayout>
  );
};

export default OnContractAnalytics;
